// Frozen copy of main's code for the document_generation_v4_enabled / agency_brand_design_enabled flag-off path; delete with the flag.
import React from 'react';
import { ApolloError, type FetchResult } from '@apollo/client';
import { client } from '~/api/client';
import {
  UpdateCaraWorkspaceArtifactDocument,
  UpdateCaraWorkspaceArtifactMutation,
  UpdateCaraWorkspaceArtifactMutationVariables,
  type VisibilityConfigInput
} from '~/api/graphql';
import type { SaveStatus } from '~/components/document-editor/artifact-focus-html-document-save-status-indicator';

// Mutations run with errorPolicy 'all', so a refused save resolves with errors instead of
// rejecting; without this the editor would report it as saved.
const throwOnMutationErrors = (result: FetchResult) => {
  if (result.errors?.length) throw new ApolloError({ graphQLErrors: result.errors });
};

/**
 * Hook for handling artifact editor updates
 * Provides save status and update handler for editing HTML documents
 */
export const useArtifactEditor = (props: {
  workspaceId: string;
  artifactId: string;
  version: number;
}) => {
  const [saveStatus, setSaveStatus] = React.useState<SaveStatus>('idle');

  const handleUpdate = React.useCallback(
    async (content: string) => {
      // Guard against invalid props
      if (!props.workspaceId || !props.artifactId || !props.version) {
        return;
      }

      const result = await client.mutate<
        UpdateCaraWorkspaceArtifactMutation,
        UpdateCaraWorkspaceArtifactMutationVariables
      >({
        mutation: UpdateCaraWorkspaceArtifactDocument,
        variables: {
          workspaceId: props.workspaceId,
          artifactId: props.artifactId,
          version: props.version,
          input: {
            document: {
              content
            }
          }
        }
      });
      throwOnMutationErrors(result);
    },
    [props.workspaceId, props.artifactId, props.version]
  );

  const handleTitleUpdate = React.useCallback(
    async (title: string) => {
      if (!props.workspaceId || !props.artifactId || !props.version) {
        return;
      }

      setSaveStatus('saving');
      try {
        const result = await client.mutate<
          UpdateCaraWorkspaceArtifactMutation,
          UpdateCaraWorkspaceArtifactMutationVariables
        >({
          mutation: UpdateCaraWorkspaceArtifactDocument,
          variables: {
            workspaceId: props.workspaceId,
            artifactId: props.artifactId,
            version: props.version,
            input: {
              document: {
                title
              }
            }
          }
        });
        throwOnMutationErrors(result);
        setSaveStatus('saved');
        setTimeout(() => setSaveStatus('idle'), 2000);
      } catch {
        setSaveStatus('error');
      }
    },
    [props.workspaceId, props.artifactId, props.version]
  );

  const handleVisibilityUpdate = React.useCallback(
    async (visibility: VisibilityConfigInput) => {
      if (!props.workspaceId || !props.artifactId || !props.version) {
        return;
      }

      const result = await client.mutate<
        UpdateCaraWorkspaceArtifactMutation,
        UpdateCaraWorkspaceArtifactMutationVariables
      >({
        mutation: UpdateCaraWorkspaceArtifactDocument,
        variables: {
          workspaceId: props.workspaceId,
          artifactId: props.artifactId,
          version: props.version,
          input: { visibility }
        }
      });
      throwOnMutationErrors(result);
    },
    [props.workspaceId, props.artifactId, props.version]
  );

  return {
    saveStatus,
    setSaveStatus,
    handleUpdate,
    handleTitleUpdate,
    handleVisibilityUpdate
  };
};
